<template>
  <div>
    <h4>
      <i18n-t keypath="documentation.api.example.title" tag="span" scope="global">
        <template #type>
          {{ $t(`documentation.api.example.${type}`) }}
        </template>
      </i18n-t>
      <span v-if="exampleGeneratorArgs.title"><span class="mx-2">»</span>{{ exampleGeneratorArgs.title }}</span>
    </h4>
    <tabbed-code-snippets v-if="canGenerateCodeSample">
      <template v-if="canSelectSkipNonRequired" #tabs>
        <v-tab :label="$t('documentation.api.example.allFields')" @click="activePanel = 0" />
        <v-tab :label="$t('documentation.api.example.requiredFieldsOnly')" @click="activePanel = 1" />
      </template>
      <template #content>
        <div v-show="activePanel === 0">
          <code-snippet
            :key="`${allFieldsSnippet.lang}:${allFieldsSnippet.code}`"
            :lang="allFieldsSnippet.lang"
            :code="allFieldsSnippet.code"
            :encoded="false"
            no-tooltip
          />
        </div>
        <div v-if="canSelectSkipNonRequired" v-show="activePanel === 1">
          <code-snippet
            :key="`${requiredFieldsSnippet.lang}:${requiredFieldsSnippet.code}`"
            :lang="requiredFieldsSnippet.lang"
            :code="requiredFieldsSnippet.code"
            :encoded="false"
            no-tooltip
          />
        </div>
      </template>
    </tabbed-code-snippets>
    <v-note v-else connotation="warning">
      {{ $t('documentation.api.example.notFound') }} <code>{{ exampleGeneratorArgs.type }}</code>
    </v-note>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref, watch } from 'vue';
import { ExampleGeneratorArgs } from '@bifrost-dev-docs-mfe/common/types/apiDocs';
import { VNote, VTab } from '@vonage/vivid-vue';
import CodeSnippet from '@/components/markdown/widgets/CodeSnippet.vue';
import TabbedCodeSnippets from '@/components/widgets/TabbedCodeSnippets.vue';
import { getCodeSamplesForSchema, isContentTypeSupported } from '@/utils/docs/api/examples';

const props = defineProps({
  type: { type: String as PropType<'request' | 'payload' | 'response'>, required: true },
  exampleGeneratorArgs: { type: Object as PropType<ExampleGeneratorArgs>, required: true },
});

const activePanel = ref(0);
// Switching to "Required fields only" should only be possible for request schemas
const canSelectSkipNonRequired = computed(() => props.type === 'request');
const canGenerateCodeSample = computed(() => isContentTypeSupported(props.exampleGeneratorArgs.type));

watch(canSelectSkipNonRequired, canSelect => {
  if (!canSelect) activePanel.value = 0;
});

const allFieldsSnippet = computed(() =>
  getCodeSamplesForSchema(
    props.exampleGeneratorArgs.schema,
    props.exampleGeneratorArgs.url,
    props.exampleGeneratorArgs.path,
    props.exampleGeneratorArgs.type,
    false
  )
);

const requiredFieldsSnippet = computed(() =>
  getCodeSamplesForSchema(
    props.exampleGeneratorArgs.schema,
    props.exampleGeneratorArgs.url,
    props.exampleGeneratorArgs.path,
    props.exampleGeneratorArgs.type,
    true
  )
);
</script>
